import { throttle, isEmpty } from 'lodash-es'
import { datadogRum } from '@datadog/browser-rum'
import { getCookie } from 'cookies-next'
import { AnalyticsBrowser } from '@segment/analytics-next'
import { isImpersonationSession } from 'lib/helpers/isImpersonationSession/isImpersonationSession'
import { isSSR } from 'lib/helpers/isSSR/isSSR'
import { pruneOversizedSegmentQueues } from './helpers/pruneOversizedSegmentQueues/pruneOversizedSegmentQueues'
import type { UserTraits } from './types'

type Properties = Record<string, unknown>
interface Params {
  includingIntercom?: boolean
}

pruneOversizedSegmentQueues()

const segmentWriteKey = process.env.SEGMENT_WRITE_KEY

export const analytics = new AnalyticsBrowser()

// Loading with an empty write key makes Segment request `/seg-cdn/v1/projects//settings`, which its
// CDN rejects with "Cannot GET - Invalid path or write key provided." on every page load. The key is
// blank in `.env.example` on purpose, so local sessions never write into the production source.
if (segmentWriteKey) {
  analytics.load(
    {
      writeKey: segmentWriteKey,
      cdnURL: isSSR() ? 'https://cdn.segment.com' : '/seg-cdn',
    },
    {
      obfuscate: true,
      integrations: {
        'Segment.io': {
          apiHost: isSSR() ? 'api.segment.io/v1' : `${window.location.host}/seg-api/v1`,
          protocol: isSSR() || window.location.protocol === 'https:' ? 'https' : 'http',
          // Must not be `omit`. Vercel Deployment Protection answers ahead of the proxy on protected
          // deployments and redirects any request without its own cookie to an origin that sends no
          // CORS headers, so a cookieless event is never delivered there. Keeping the app's cookies
          // off Segment is the proxy's job instead: it drops them before the upstream hop.
          deliveryStrategy: { config: { credentials: 'same-origin' } },
        },
      },
    },
  )
}

export const isTrackingDisabled = () => !segmentWriteKey || isImpersonationSession() || isSSR()

const includingIntercom = {
  All: true,
  Intercom: true,
}

const exceptIntercom = {
  ...includingIntercom,
  // filtering for Intercom destination inside of Segment is available only on expensive plan,
  // therefore this flag lets Segment know that "track" events should not be sent to Intercom
  Intercom: false,
}

const getFacebookContext = () => {
  const fbc = getCookie('_fbc')?.toString() || null
  const fbp = getCookie('_fbp')?.toString() || null

  return { fbc, fbp }
}

const getExtraContext = () => ({
  facebook: getFacebookContext(),
})

const logToConsoleIfNeeded = (event: string, properties: Properties = {}) => {
  if (isSSR() || !window.SHOW_EVENTS_IN_CONSOLE) {
    return
  }

  // eslint-disable-next-line no-console
  console.log(event, isEmpty(properties) ? undefined : properties)
}

export const identifyUser = async (userId: string, traits: UserTraits) => {
  if (isTrackingDisabled()) {
    return
  }

  try {
    await analytics?.identify(
      userId,
      traits,
      { integrations: includingIntercom, context: getExtraContext() },
      () => window.Intercom?.('update'),
    )
  } catch (err) {
    console.error(err)
  }
}

export const trackPage = async (name = 'Page view.', properties: Properties = {}) => {
  logToConsoleIfNeeded(name, properties)

  if (isTrackingDisabled()) {
    return
  }

  try {
    await analytics?.page(name, properties, { integrations: includingIntercom, context: getExtraContext() })
  } catch (err) {
    console.error(err)
  }
}

export const logEvent = async (event: string, properties: Properties = {}, params: Params = {}) => {
  datadogRum.addAction(event, properties)
  logToConsoleIfNeeded(event, properties)

  if (isTrackingDisabled()) {
    return
  }

  try {
    await analytics?.track(event, properties, {
      integrations: params.includingIntercom ? includingIntercom : exceptIntercom,
      context: getExtraContext(),
    })
  } catch (err) {
    console.error(err)
  }
}

export const resetTracking = () => {
  if (isTrackingDisabled()) {
    return
  }

  analytics?.reset()
  window.Intercom?.('shutdown')
}

export const logEventThrottled = throttle(logEvent, 1000, { leading: false })
